import { useTranslation } from 'react-i18next';
import { CheckCircle2, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { KbProfileFormValues } from '@/hooks/useKbProfileForm';
import { OFFERS, MODALITIES, OBJECTIVES, PAYMENT_METHODS } from '@/hooks/useKbProfileForm';
import type { Offer, Modality, Objective } from '@/services/kbProfileService';

interface ReviewStepProps {
  values: KbProfileFormValues;
  onActivate: () => void;
  isSubmitting: boolean;
  submitError: string | null;
}

function ReviewRow({ label, value }: { label: string; value?: string }) {
  if (!value) return null;
  return (
    <div className="flex flex-col sm:flex-row gap-1 py-2 border-b border-border last:border-0">
      <dt className="text-xs text-muted-foreground sm:w-40 shrink-0">{label}</dt>
      <dd className="text-sm min-w-0 break-words">{value}</dd>
    </div>
  );
}

export function ReviewStep({ values, onActivate, isSubmitting, submitError }: ReviewStepProps) {
  const { t } = useTranslation();

  const offerLabels = (values.offers ?? [])
    .filter((o): o is Offer => OFFERS.includes(o as Offer))
    .map((o) => t(`onboarding.kbForm.offer_${o}`))
    .join(', ');

  const modalityLabels = (values.modalities ?? [])
    .filter((m): m is Modality => MODALITIES.includes(m as Modality))
    .map((m) => t(`onboarding.kbForm.modality_${m}`))
    .join(', ');

  const objectiveLabel = values.bot_goal
    ? OBJECTIVES.includes(values.bot_goal as Objective)
      ? t(`onboarding.kbForm.objective_${values.bot_goal}`)
      : values.bot_goal
    : '';

  const catalogSummary =
    (values.catalog ?? [])
      .filter((item) => item.name?.trim())
      .map((item) => item.name.trim())
      .join(', ') || undefined;

  const paymentMethods =
    [
      // Known methods → translated label; unknown → raw (free-add values)
      ...(values.operation?.payment_methods ?? []).map((m) =>
        (PAYMENT_METHODS as readonly string[]).includes(m) ? t(`onboarding.kbForm.payment_${m}`) : m
      ),
      ...(values.operation?.payment_methods_other ?? []),
    ]
      .filter(Boolean)
      .join(', ') || undefined;

  return (
    <div className="space-y-6">
      <div>
        <p className="inline-flex items-center gap-2 text-lg font-semibold">
          <CheckCircle2 className="h-5 w-5 text-success shrink-0" aria-hidden="true" />
          {t('onboarding.kbForm.step6.headline')}
        </p>
        <p className="text-sm text-muted-foreground mt-1">{t('onboarding.kbForm.step6.subline')}</p>
      </div>

      <dl className="space-y-0">
        <ReviewRow label={t('onboarding.kbForm.step6.review_offers')} value={offerLabels} />
        <ReviewRow label={t('onboarding.kbForm.step6.review_modalities')} value={modalityLabels} />
        <ReviewRow
          label={t('onboarding.kbForm.step6.review_one_liner')}
          value={values.one_liner || undefined}
        />
        <ReviewRow
          label={t('onboarding.kbForm.step6.review_ideal_customer')}
          value={values.ideal_customer || undefined}
        />
        <ReviewRow label={t('onboarding.kbForm.step6.review_catalog')} value={catalogSummary} />
        <ReviewRow
          label={t('onboarding.kbForm.step6.review_objective')}
          value={objectiveLabel || undefined}
        />
        <ReviewRow
          label={t('onboarding.kbForm.step6.review_handoff')}
          value={values.handoff_contact || undefined}
        />
        <ReviewRow label={t('onboarding.kbForm.step6.review_payments')} value={paymentMethods} />
      </dl>

      {submitError && (
        <div role="alert" className="text-sm text-destructive">
          {submitError}
        </div>
      )}

      <Button
        type="button"
        className="w-full sm:w-auto h-11"
        disabled={isSubmitting}
        onClick={onActivate}
        data-testid="activate-button"
      >
        {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        {isSubmitting
          ? t('onboarding.kbForm.step6.activating')
          : t('onboarding.kbForm.step6.activate_cta')}
      </Button>

      <p className="text-xs text-muted-foreground">
        {t('onboarding.kbForm.step6.enrichment_hint')}
      </p>
    </div>
  );
}
